import React, {
  useMemo,
  useReducer,
  type FC,
  type PropsWithChildren,
  useRef,
  useEffect,
} from 'react'
import {
  MultiBuyDispatcher,
  dispatcherContext,
  multiBuyListCountsStoreContext,
  stateContext,
} from './Context'
import { Status } from '../../components/Status'
import { createSetErrorAction } from './Actions'
import { reducer } from './Reducer'
import { createMultiBuyListCountsStore } from './createMultiBuyListCountsStore'
import { useQuery } from '@moonpig/web-core-graphql'
import { GetMultiBuyListCountGQL } from '../../queries/useMultiBuyList'
import { GetMultiBuyListCountQuery } from '../../queries/useMultiBuyList/__generated__/getMultiBuyListCountGQL'

type Props = {
  enabled: boolean
  subscribeToList?: boolean
}

export const MultiBuyProvider: FC<PropsWithChildren<Props>> = ({
  enabled,
  subscribeToList = false,
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    enabled,
    error: false,
    errorMessage: undefined,
  })

  const countsStoreRef = useRef(createMultiBuyListCountsStore())
  const countsStore = countsStoreRef.current

  const { data: multiBuyListData, loading } =
    useQuery<GetMultiBuyListCountQuery>(GetMultiBuyListCountGQL, {
      ssr: false,
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
      skip: !subscribeToList,
    })

  const multiBuyListItems = multiBuyListData?.me.multiBuyList?.items

  useEffect(() => {
    if (!subscribeToList) {
      countsStore.getState().reset()
      return
    }
    if (loading) return
    countsStore.getState().setCountFromItems(multiBuyListItems ?? [])
  }, [subscribeToList, loading, multiBuyListItems, countsStore])

  const actions = useMemo<MultiBuyDispatcher>(
    () => ({
      setError: createSetErrorAction(dispatch),
    }),
    [],
  )

  const contextValue = useMemo(
    () => ({
      ...state,
      enabled,
    }),
    [state, enabled],
  )

  return (
    <multiBuyListCountsStoreContext.Provider value={countsStore}>
      <dispatcherContext.Provider value={actions}>
        <stateContext.Provider value={contextValue}>
          <Status
            error={{
              status: state.error,
              message: state.errorMessage,
              onDismiss: () =>
                actions.setError({ error: false, errorMessage: undefined }),
            }}
          />
          {children}
        </stateContext.Provider>
      </dispatcherContext.Provider>
    </multiBuyListCountsStoreContext.Provider>
  )
}
